import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { Search } from "lucide-react";
import { motion } from "framer-motion";
import { getAgencies } from "../../../api/auth";
import Loading from "../../ui/Loading";
import ServiceUnavailable from "../../component/ServiceUnavailable";
import ameex from "../../../png livraison/ameex.png";
import aramex from "../../../png livraison/aramex.png";
import bmdtm from "../../../png livraison/bmdtm.png";
import cathedis from "../../../png livraison/cathedis.png";
import digylog from "../../../png livraison/digylog.png";
import forcelog from "../../../png livraison/forcelog.png";
import olivraison from "../../../png livraison/olivraison.png";
import ozonexpress from "../../../png livraison/ozonexpress.png";

const deliveryCompanies = [
  { code: "OZON_EXPRESS", name: "Ozon Express", logo: ozonexpress },
  { code: "OLIVRAISON", name: "Olivraison", logo: olivraison },
  { code: "AMEEX", name: "Ameex", logo: ameex },
  { code: "SENDIT", name: "Sendit", logo: null },
  { code: "ARAMEX", name: "Aramex", logo: aramex },
  { code: "BMDTM", name: "BMDTM", logo: bmdtm },
  { code: "CATHEDIS", name: "Cathedis", logo: cathedis },
  { code: "DIGYLOG", name: "Digylog", logo: digylog },
  { code: "FORCELOG", name: "Forcelog", logo: forcelog },
  { code: "ONESTA", name: "ONESSTA", logo: null },
];

export default function LivraisonAdd() {
  const [search, setSearch] = useState("");
  const [agencies, setAgencies] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const navigate = useNavigate();

  useEffect(() => {
    loadAgencies();
  }, []);

  const loadAgencies = async () => {
    try {
      setLoading(true);
      setError(null);
      const data = await getAgencies();
      setAgencies(data || []);
    } catch (err) {
      console.error(err);
      setAgencies([]);
      setError(err);
    } finally {
      setLoading(false);
    }
  };

  const isAgencyActive = (code) => {
    return agencies.find(
      (a) => a.code?.toUpperCase() === code?.toUpperCase() && a.is_active
    );
  };

  const filtered = deliveryCompanies
    .filter((c) => c.name.toLowerCase().includes(search.toLowerCase()))
    .sort((a, b) => {
      const aActive = isAgencyActive(a.code) ? 0 : 1;
      const bActive = isAgencyActive(b.code) ? 0 : 1;
      return aActive - bActive;
    });

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-50 dark:bg-slate-950">
        <Loading fullScreen text="Loading delivery companies..." variant="dots" />
      </div>
    );
  }

  if (error) {
    return (
      <div className="min-h-screen bg-gray-50 p-6 dark:bg-slate-950">
        <div className="mx-auto max-w-3xl">
          <ServiceUnavailable label="Le service de livraison" onRetry={loadAgencies} />
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50 p-6 dark:bg-slate-950">
      <div className="mx-auto max-w-5xl space-y-8">
        <div>
          <h1 className="text-3xl font-semibold text-gray-800 dark:text-slate-100">
            Add Delivery Company
          </h1>
          <p className="text-gray-500 dark:text-slate-500">
            Connect your delivery partners
          </p>
        </div>

        <div className="relative">
          <Search className="absolute left-3 top-3 text-gray-400 dark:text-slate-500" size={18} />
          <input
            placeholder="Search delivery company..."
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            className="w-full rounded-xl border py-3 pl-10 pr-4 dark:bg-slate-800 dark:border-slate-700 dark:text-slate-100"
          />
        </div>

        <div className="grid grid-cols-2 gap-5 md:grid-cols-3 lg:grid-cols-4">
          {filtered.map((company) => {
            const activeAgency = isAgencyActive(company.code);

            return (
              <motion.div
                key={company.code}
                whileHover={activeAgency ? { scale: 1.05 } : {}}
                onClick={() =>
                  activeAgency && navigate(`/delivery/connect/${company.code}`)
                }
                className={`rounded-xl border p-5 text-center transition ${
                  activeAgency
                    ? "cursor-pointer bg-white hover:shadow-md dark:bg-slate-900 dark:border-slate-800"
                    : "cursor-not-allowed bg-gray-100 opacity-50 dark:bg-slate-800 dark:border-slate-800"
                }`}
              >
                <img
                  src={company.logo}
                  alt={company.name}
                  className="mx-auto mb-3 h-12 object-contain"
                />

                <p className="font-medium text-gray-800 dark:text-slate-100">{company.name}</p>

                {!activeAgency && (
                  <p className="mt-1 text-xs text-gray-400 dark:text-slate-500">
                    Coming Soon
                  </p>
                )}
              </motion.div>
            );
          })}
        </div>
      </div>
    </div>
  );
}